(ns arthur.demo.take
  "The synthetic take: the whole vertical slice, with no video file in it.

  This is port-plan step 5's deliverable. `flow/take` now composes the shared
  measurement path for both this generator and real footage —

      synth  ──▶  measure/anchor  ──▶  condition/anchor
                        │                     │
                        └──▶  mouth, eyes, brows ◀─┘
                                   │
                              condition/parts
                                   │
                                 FREEZE  ──▶  channels on nodes
                                                    │
                                          symbol/resolver ──▶ raster

  — and the order of that diagram is the whole argument for the stage split. The
  anchor fit is knob-free. Conditioning smooths its four parameters. The rings are
  then measured THROUGH the conditioned transform, so `anchor avg` does re-run the
  ring mapping — a few hundred frames of twenty points, free — and does not re-run
  anything that reads a source pixel, because that part takes the landmarks and
  the frames and never the transform.

  TWO CLIPS, ONE STORE. `:take` reads each measured head transform in time;
  `:take-locked` holds the measured transform from frame zero. They share the
  same dense blocks; only the head node's anchor map differs."
  (:require [arthur.flow.address :as address]
            [arthur.flow.freeze :as freeze]
            [arthur.flow.take :as take]
            [arthur.synth :as synth]))

(def frames 229)
(def fps 30)

(def ^:private stage
  ;; The project's dimensions, and NOT the footage's. This is what deleting
  ;; `makeXform` buys: the head is placed and scaled on the stage by a transform
  ;; on a node, so a 1440x1920 portrait clip and a 320x200 stage are not a
  ;; conflict to resolve. Whatever hangs off the edge is clipped.
  [320 200])

(def ^:private aspect
  ;; The synth writes x and y in the SAME unit, so its normalised space is already
  ;; isotropic and the anisotropy correction is the identity here. Real footage
  ;; passes W/H from the manifest at step 6. Worth knowing while reading anything
  ;; here: aspect 1 is the one setting at which a port that dropped the
  ;; anisotropy correction entirely would still look right, which is why
  ;; anchor-test exercises 0.5625 and this does not.
  1)

(def analysis
  "Stage 2's output, synthesised. A seeded generator, so a wrong pose is
  reproducible rather than something that happened once."
  (delay (synth/synth-dense frames {:seed 1})))

(def measured
  "Stages 3 and 4, in the order the stage split requires."
  (delay (take/measure (assoc take/knobs :aspect aspect :fps fps)
                       {:dense @analysis})))

(def params
  "What the freeze was handed. Public because it is the honest way to re-freeze at
  other settings — a test that built its own copy would be asserting about a clip
  nobody looks at."
  (merge take/knobs
         {:name   "take"
          :fps    fps
          :stage  stage
          ;; On 2s. docs/design.md is emphatic that everything rides ONE grid: a
          ;; head cutting on odd frames against a mouth cutting on even ones reads
          ;; as two performances, so exposure lives on the clip root and inherits.
          :expose 2
          ;; The head as filmed. `:take-locked` is the same freeze with this one
          ;; field changed, which is the point.
          :head   :free
          ;; Provenance, and now a content address. There is no detector here, so
          ;; the generator IS the detector and its seed is the source: two synth
          ;; takes at different seeds are different analyses, which is the same
          ;; statement content addressing makes about two model versions.
          :analysis (address/analysis {:detector "synth"
                                      :version  "mulberry32"
                                      :seed     1
                                      :frames   frames
                                      :fps      fps
                                      :aspect   aspect})}))

(def subject :face-1)

(def frozen
  (delay (freeze/clip params {subject @measured})))

(def store (delay (:store @frozen)))

(def clip (delay (:clip @frozen)))

(def locked
  "The same blocks, with `:head` held at measured frame zero."
  (delay (freeze/head-mode {:trace {:origin :start}} @frozen)))
